// CoD service — Phase 2 implementation
// Uses app_config (key-value store) instead of the non-existent cod_config table.
//
// Keys stored in app_config:
//   cod_enabled              boolean ("true"/"false")
//   cod_min_amount           number  (FCFA, default 2000)
//   cod_max_amount           number  (FCFA, default 150000)
//   cod_allowed_zones        JSON array of zone slug strings
//   cod_require_phone_verify boolean ("true"/"false")
//   cod_max_pending_orders   number  (default 3)
//
// cod_orders and cod_verifications are separate tables (unrelated to cod_config).

import { supabase } from "@/lib/supabase";
import {
  CodOrderStatus,
  type CodConfig,
  type CodEligibilityResult,
  type CodOrder,
  type CodVerification,
} from "./types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SUPABASE_AVAILABLE = Boolean(import.meta.env.VITE_SUPABASE_URL);

// app_config keys used by CoD
const APP_CONFIG_KEYS = [
  "cod_enabled",
  "cod_min_amount",
  "cod_max_amount",
  "cod_allowed_zones",
  "cod_require_phone_verify",
  "cod_max_pending_orders",
] as const;

// ---------------------------------------------------------------------------
// Default configuration (used by mock and as fallback when keys missing)
// ---------------------------------------------------------------------------

export const DEFAULT_COD_CONFIG: CodConfig = {
  enabled: true,
  minAmount: 2000,
  maxAmount: 150000,
  allowedZones: [
    "dakar",
    "thies-mbour",
    "kaolack",
    "saint-louis",
    "fatick",
    "diourbel-touba",
  ],
  requirePhoneVerification: true,
  maxPendingOrders: 3,
} as const;

// ---------------------------------------------------------------------------
// In-memory stores (mock — only used when VITE_SUPABASE_URL is not set)
// ---------------------------------------------------------------------------

const mockVerifications = new Map<string, CodVerification>();
const mockOrders = new Map<string, CodOrder>();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate a random 4-digit numeric code */
function generateCode(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

/** Count pending CoD orders for a given phone number — mock path only */
function mockCountPendingOrders(phone: string): number {
  let count = 0;
  mockOrders.forEach((order) => {
    const isPending =
      order.status === CodOrderStatus.PENDING_VERIFICATION ||
      order.status === CodOrderStatus.VERIFIED ||
      order.status === CodOrderStatus.OUT_FOR_DELIVERY;
    if (order.customerPhone === phone && isPending) {
      count += 1;
    }
  });
  return count;
}

/**
 * Parse a raw app_config value string into the typed CodConfig.
 * Each key maps directly to a config field.
 */
function parseAppConfigRows(
  rows: ReadonlyArray<{ key: string; value: unknown }>,
): CodConfig {
  const map = new Map(rows.map((r) => [r.key, r.value]));

  const raw = (key: string, fallback: unknown) => map.get(key) ?? fallback;

  // Values stored in Supabase jsonb come back as JS types (boolean, number, array).
  // Values stored as text come back as strings and need coercion.
  function toBool(v: unknown, fallback: boolean): boolean {
    if (typeof v === "boolean") return v;
    if (v === "true" || v === true) return true;
    if (v === "false" || v === false) return false;
    return fallback;
  }

  function toNumber(v: unknown, fallback: number): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function toStringArray(v: unknown, fallback: string[]): string[] {
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === "string") {
      try {
        const parsed = JSON.parse(v);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        // ignore
      }
    }
    return fallback;
  }

  return {
    enabled: toBool(raw("cod_enabled", DEFAULT_COD_CONFIG.enabled), DEFAULT_COD_CONFIG.enabled),
    minAmount: toNumber(raw("cod_min_amount", DEFAULT_COD_CONFIG.minAmount), DEFAULT_COD_CONFIG.minAmount),
    maxAmount: toNumber(raw("cod_max_amount", DEFAULT_COD_CONFIG.maxAmount), DEFAULT_COD_CONFIG.maxAmount),
    allowedZones: toStringArray(raw("cod_allowed_zones", DEFAULT_COD_CONFIG.allowedZones), [...DEFAULT_COD_CONFIG.allowedZones]),
    requirePhoneVerification: toBool(
      raw("cod_require_phone_verify", DEFAULT_COD_CONFIG.requirePhoneVerification),
      DEFAULT_COD_CONFIG.requirePhoneVerification,
    ),
    maxPendingOrders: toNumber(
      raw("cod_max_pending_orders", DEFAULT_COD_CONFIG.maxPendingOrders),
      DEFAULT_COD_CONFIG.maxPendingOrders,
    ),
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Return the current CoD configuration, read from app_config */
export async function getCodConfig(): Promise<CodConfig> {
  if (!SUPABASE_AVAILABLE) {
    return DEFAULT_COD_CONFIG;
  }

  const { data, error } = await supabase
    .from("app_config")
    .select("key, value")
    .in("key", APP_CONFIG_KEYS);

  if (error || !data || data.length === 0) {
    // Keys not seeded yet — return default
    return DEFAULT_COD_CONFIG;
  }

  return parseAppConfigRows(data);
}

/**
 * Persist an updated CoD configuration to app_config (upsert all CoD keys).
 * No-op in mock mode.
 */
export async function saveCodConfig(config: CodConfig): Promise<void> {
  if (!SUPABASE_AVAILABLE) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log("[DEV] saveCodConfig (mock — no Supabase):", config);
    }
    return;
  }

  const rows = [
    { key: "cod_enabled", value: config.enabled },
    { key: "cod_min_amount", value: config.minAmount },
    { key: "cod_max_amount", value: config.maxAmount },
    { key: "cod_allowed_zones", value: config.allowedZones },
    { key: "cod_require_phone_verify", value: config.requirePhoneVerification },
    { key: "cod_max_pending_orders", value: config.maxPendingOrders },
  ];

  const { error } = await supabase
    .from("app_config")
    .upsert(rows, { onConflict: "key" });

  if (error) {
    throw new Error(`Failed to save CoD config: ${error.message}`);
  }
}

/**
 * Check whether a customer is eligible for Cash on Delivery.
 *
 * Rules checked:
 * 1. CoD is globally enabled
 * 2. Order amount falls within [minAmount, maxAmount]
 * 3. Delivery zone is in the allowed list
 * 4. Customer has not exceeded max pending CoD orders
 */
export async function checkCodEligibility(
  amount: number,
  zone: string,
  customerPhone: string,
): Promise<CodEligibilityResult> {
  const config = await getCodConfig();

  if (!config.enabled) {
    return { eligible: false, reason: "cod_disabled" };
  }

  if (amount < config.minAmount) {
    return { eligible: false, reason: "min_amount", threshold: config.minAmount };
  }

  if (amount > config.maxAmount) {
    return { eligible: false, reason: "max_amount", threshold: config.maxAmount };
  }

  if (!config.allowedZones.includes(zone)) {
    return { eligible: false, reason: "zone_restricted" };
  }

  // Count pending orders — Supabase or mock
  let pendingCount: number;

  if (SUPABASE_AVAILABLE) {
    const pendingStatuses = [
      CodOrderStatus.PENDING_VERIFICATION,
      CodOrderStatus.VERIFIED,
      CodOrderStatus.OUT_FOR_DELIVERY,
    ];

    const { count, error } = await supabase
      .from("cod_orders")
      .select("order_id", { count: "exact", head: true })
      .eq("customer_phone", customerPhone)
      .in("status", pendingStatuses);

    if (error) {
      // Fail open — let the order proceed if count query fails
      pendingCount = 0;
    } else {
      pendingCount = count ?? 0;
    }
  } else {
    pendingCount = mockCountPendingOrders(customerPhone);
  }

  if (pendingCount >= config.maxPendingOrders) {
    return { eligible: false, reason: "max_pending" };
  }

  return { eligible: true };
}

/**
 * Create a new CoD order and, if phone verification is required,
 * generate a verification code and send it via SMS.
 *
 * Returns a new immutable CodOrder with status PENDING_VERIFICATION
 * (or VERIFIED if phone verification is disabled).
 */
export async function initiateCodOrder(order: CodOrder): Promise<CodOrder> {
  const config = await getCodConfig();
  const code = generateCode();

  const initialStatus = config.requirePhoneVerification
    ? CodOrderStatus.PENDING_VERIFICATION
    : CodOrderStatus.VERIFIED;

  const newOrder: CodOrder = {
    ...order,
    status: initialStatus,
    verificationCode: code,
  };

  if (SUPABASE_AVAILABLE) {
    const { error } = await supabase.from("cod_orders").insert({
      order_id: newOrder.orderId,
      customer_phone: newOrder.customerPhone,
      amount: newOrder.amount,
      delivery_address: newOrder.deliveryAddress,
      status: newOrder.status,
      verification_code: newOrder.verificationCode,
    });

    if (error) {
      throw new Error(`Failed to create CoD order: ${error.message}`);
    }

    if (config.requirePhoneVerification) {
      const verification: CodVerification = {
        orderId: newOrder.orderId,
        code,
        phone: newOrder.customerPhone,
        expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
        verified: false,
      };

      const { error: verifyError } = await supabase
        .from("cod_verifications")
        .insert({
          order_id: verification.orderId,
          code: verification.code,
          phone: verification.phone,
          expires_at: verification.expiresAt,
          verified: verification.verified,
        });

      if (verifyError) {
        throw new Error(`Failed to create CoD verification: ${verifyError.message}`);
      }

      await sendVerificationSms(newOrder.customerPhone, code);
    }
  } else {
    // Mock path
    mockOrders.set(newOrder.orderId, newOrder);

    if (config.requirePhoneVerification) {
      const verification: CodVerification = {
        orderId: newOrder.orderId,
        code,
        phone: newOrder.customerPhone,
        expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
        verified: false,
      };
      mockVerifications.set(newOrder.orderId, verification);
      await sendVerificationSms(newOrder.customerPhone, code);
    }
  }

  return newOrder;
}

/**
 * Send a 4-digit verification code via SMS.
 * In dev/mock mode: logs to console only.
 * TODO (Phase 3): Integrate with SMS gateway (e.g. Senegalese operator API or Vonage).
 */
export async function sendVerificationSms(
  phone: string,
  code: string,
): Promise<boolean> {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[DEV SMS] Code ${code} → ${phone}`);
  }
  return true;
}

/**
 * Validate a verification code for a given order.
 * Returns true if the code matches and has not expired.
 */
export async function verifyCode(
  orderId: string,
  code: string,
): Promise<boolean> {
  if (SUPABASE_AVAILABLE) {
    const { data, error } = await supabase
      .from("cod_verifications")
      .select("code, expires_at, verified")
      .eq("order_id", orderId)
      .single();

    if (error || !data) return false;
    if (data.verified) return true;
    if (new Date(data.expires_at) < new Date()) return false;
    if (data.code !== code) return false;

    // Mark verified
    await supabase
      .from("cod_verifications")
      .update({ verified: true })
      .eq("order_id", orderId);

    // Advance order to VERIFIED
    await supabase
      .from("cod_orders")
      .update({ status: CodOrderStatus.VERIFIED })
      .eq("order_id", orderId);

    return true;
  }

  // Mock path
  const verification = mockVerifications.get(orderId);

  if (!verification) return false;
  if (verification.verified) return true;
  if (new Date(verification.expiresAt) < new Date()) return false;
  if (verification.code !== code) return false;

  mockVerifications.set(orderId, { ...verification, verified: true });

  const order = mockOrders.get(orderId);
  if (order) {
    mockOrders.set(orderId, { ...order, status: CodOrderStatus.VERIFIED });
  }

  return true;
}

/**
 * Delivery agent confirms cash collection.
 * Returns the updated order with DELIVERED_PAID or DELIVERED_UNPAID status.
 */
export async function confirmDelivery(
  orderId: string,
  agentId: string,
  collectedAmount: number,
): Promise<CodOrder> {
  if (SUPABASE_AVAILABLE) {
    const { data: existing, error: fetchError } = await supabase
      .from("cod_orders")
      .select("*")
      .eq("order_id", orderId)
      .single();

    if (fetchError || !existing) {
      throw new Error(`CoD order not found: ${orderId}`);
    }

    const isPaid = collectedAmount >= existing.amount;
    const newStatus = isPaid
      ? CodOrderStatus.DELIVERED_PAID
      : CodOrderStatus.DELIVERED_UNPAID;
    const confirmedAt = new Date().toISOString();

    const { error: updateError } = await supabase
      .from("cod_orders")
      .update({
        status: newStatus,
        delivery_agent_id: agentId,
        collected_amount: collectedAmount,
        confirmed_at: confirmedAt,
      })
      .eq("order_id", orderId);

    if (updateError) {
      throw new Error(`Failed to confirm CoD delivery: ${updateError.message}`);
    }

    return {
      orderId: existing.order_id,
      customerPhone: existing.customer_phone,
      amount: existing.amount,
      deliveryAddress: existing.delivery_address,
      status: newStatus,
      verificationCode: existing.verification_code,
      deliveryAgentId: agentId,
      collectedAmount,
      confirmedAt,
    };
  }

  // Mock path
  const order = mockOrders.get(orderId);

  if (!order) {
    throw new Error(`CoD order not found: ${orderId}`);
  }

  const isPaid = collectedAmount >= order.amount;

  const updatedOrder: CodOrder = {
    ...order,
    status: isPaid
      ? CodOrderStatus.DELIVERED_PAID
      : CodOrderStatus.DELIVERED_UNPAID,
    deliveryAgentId: agentId,
    collectedAmount,
    confirmedAt: new Date().toISOString(),
  };

  mockOrders.set(orderId, updatedOrder);
  return updatedOrder;
}
